import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/UserProfileAttributesService';

import {UserProfileAttribute} from '../types';

// FNP-949: records are React Query's job; selection stays in Redux (FNP-951).
//
// This module is unlike the others: the "records" are the signed-in user's
// COGNITO ATTRIBUTES, read via fetchUserAttributes rather than a GraphQL list.
// They are identified by attribute NAME, not by an id, so the detail key is
// keyed on name.
//
// There is no create: attributes exist on the Cognito user or they do not, and
// the service's `add` is a no-op that only shows a notification.

export const userProfileAttributesKeys = {
    all: ['user-profile-attributes'] as const,
    lists: () => [...userProfileAttributesKeys.all, 'list'] as const,
    details: () => [...userProfileAttributesKeys.all, 'detail'] as const,
    detail: (name: string) => [...userProfileAttributesKeys.details(), name] as const,
};

export const useUserProfileAttributesQuery = () => {
    return useQuery({
        queryKey: userProfileAttributesKeys.lists(),
        queryFn: async (): Promise<UserProfileAttribute[]> => {
            const records = await fetchRecords();
            return records ?? [];
        },
    });
};

export const useUserProfileAttributeQuery = (attributeName?: string) => {
    return useQuery({
        queryKey: userProfileAttributesKeys.detail(attributeName ?? ''),
        queryFn: () => fetchOne(attributeName as string),
        enabled: !!attributeName,
    });
};

export const useUpdateUserProfileAttributeMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: UserProfileAttribute }) => update(id, record),
        onSuccess: (_data, variables) => {
            // Invalidate the whole list: updating one Cognito attribute
            // re-reads them all, and the detail entry is derived from the list.
            queryClient.invalidateQueries({queryKey: userProfileAttributesKeys.all});
            queryClient.invalidateQueries({queryKey: userProfileAttributesKeys.detail(variables.id)});
        },
    });
};

export const useDeleteUserProfileAttributeMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: userProfileAttributesKeys.all});
        },
    });
};
